import { DeleteConversationsDialog } from "@app/components/assistant/conversation/DeleteConversationsDialog";
import { EditConversationTitleDialog } from "@app/components/assistant/conversation/EditConversationTitleDialog";
import { LeaveConversationDialog } from "@app/components/assistant/conversation/LeaveConversationDialog";
import { ConfirmContext } from "@app/components/Confirm";
import {
  useConversationParticipants,
  useConversationParticipationOptions,
  useJoinConversation,
} from "@app/hooks/conversations";
import { useDeleteConversation } from "@app/hooks/useDeleteConversation";
import { useMoveConversationToProject } from "@app/hooks/useMoveConversationToProject";
import { useSendNotification } from "@app/hooks/useNotification";
import { useURLSheet } from "@app/hooks/useURLSheet";
import config from "@app/lib/api/config";
import { useAuth, useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useAppRouter } from "@app/lib/platform";
import { getSpaceIcon } from "@app/lib/spaces";
import { useSpaces } from "@app/lib/swr/spaces";
import {
  getAgentBuilderRoute,
  getConversationRoute,
  getProjectRoute,
  setQueryParam,
} from "@app/lib/utils/router";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import { isProjectConversation } from "@app/types/assistant/conversation";
import type { WorkspaceType } from "@app/types/user";
import { isBuilder } from "@app/types/user";
import {
  ArrowRightIcon,
  Avatar,
  ContactsUserIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  ExternalLinkIcon,
  LinkIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  SidekickIcon,
  TrashIcon,
  XMarkIcon,
} from "@dust-tt/sparkle";
import type React from "react";
import type { ReactElement } from "react";
import { useCallback, useContext, useEffect, useState } from "react";

/**
 * Hook for handling right-click context menu with timing protection
 *
 * This hook solves the "double right-click" problem where right-clicking while
 * a menu is open would cause it to close and immediately reopen at the cursor position.
 *
 * The core issue: DropdownMenu doesn't add a backdrop to catch events, so right-clicks
 * while the menu is open still trigger our handlers. Due to React's async state updates,
 * when the menu closes, our right-click handler sees isMenuOpen as false and reopens the menu.
 */
export function useConversationMenu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuTriggerPosition, setMenuTriggerPosition] = useState<
    { x: number; y: number } | undefined
  >();

  // Tracks if the menu was just closed to prevent immediate reopening
  // This flag creates a brief "cooldown" period after menu closure
  const [wasMenuJustClosed, setWasMenuJustClosed] = useState(false);

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      // When menu closes, set the "just closed" flag for 100ms
      // This prevents right-click handlers from immediately reopening the menu
      setWasMenuJustClosed(true);
      setTimeout(() => {
        setWasMenuJustClosed(false);
      }, 100);
    }
  }, []);

  const handleRightClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ignore right-clicks if menu is currently open OR was just closed
      // This prevents the close -> immediate reopen behavior
      if (isMenuOpen || wasMenuJustClosed) {
        return;
      }

      // Open menu at cursor position
      setMenuTriggerPosition({ x: e.clientX, y: e.clientY });
      setIsMenuOpen(true);
    },
    [isMenuOpen, wasMenuJustClosed]
  );

  // Clear the trigger position when menu closes to allow animations to complete
  // The 150ms delay ensures smooth closing animation before position reset
  useEffect(() => {
    if (!isMenuOpen) {
      setTimeout(() => {
        setMenuTriggerPosition(undefined);
      }, 150);
    }
  }, [isMenuOpen]);

  return {
    isMenuOpen,
    menuTriggerPosition,
    handleRightClick,
    handleMenuOpenChange,
  };
}

interface ConversationMenuProps {
  activeConversationId: string | null;
  conversation?: ConversationWithoutContentType;
  owner: WorkspaceType;
  trigger: ReactElement;
  isConversationDisplayed: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  triggerPosition?: { x: number; y: number };
  displayOpenInBrowser?: boolean;
  openDetailsInNewTab?: boolean;
}

export function ConversationMenu({
  activeConversationId,
  conversation,
  owner,
  trigger,
  isConversationDisplayed,
  isOpen,
  onOpenChange,
  triggerPosition,
  displayOpenInBrowser,
  openDetailsInNewTab,
}: ConversationMenuProps) {
  const { user } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const confirm = useContext(ConfirmContext);

  const router = useAppRouter();

  const canTurnIntoAgent =
    hasFeature("agent_builder_shrink_wrap") &&
    !!conversation &&
    !!user &&
    isBuilder(owner);
  const sendNotification = useSendNotification();

  const { onOpenChange: onOpenChangeAgentModal } = useURLSheet("agentDetails");
  const { onOpenChange: onOpenChangeUserModal } = useURLSheet("userDetails");

  const handleSeeAgentDetails = (agentId: string) => {
    if (openDetailsInNewTab) {
      const agentDetailsUrl = getConversationRoute(
        owner.sId,
        activeConversationId,
        `agentDetails=${agentId}`,
        config.getApiBaseUrl()
      );
      window.open(agentDetailsUrl, "_blank");
      return;
    }
    onOpenChangeAgentModal(true);
    setQueryParam(router, "agentDetails", agentId);
  };

  const handleSeeUserDetails = (userId: string) => {
    if (openDetailsInNewTab) {
      const userDetailsUrl = getConversationRoute(
        owner.sId,
        activeConversationId,
        `userDetails=${userId}`,
        config.getApiBaseUrl()
      );
      window.open(userDetailsUrl, "_blank");
      return;
    }
    onOpenChangeUserModal(true);
    setQueryParam(router, "userDetails", userId);
  };

  const shouldWaitBeforeFetching =
    activeConversationId === null || user?.sId === undefined || !isOpen;
  const conversationParticipationOptions = useConversationParticipationOptions({
    ownerId: owner.sId,
    conversationId: activeConversationId,
    userId: user?.sId ?? null,
    disabled: shouldWaitBeforeFetching,
  });
  const { conversationParticipants } = useConversationParticipants({
    conversationId: activeConversationId,
    workspaceId: owner.sId,
    options: {
      disabled: shouldWaitBeforeFetching,
    },
  });
  const { spaces: projects } = useSpaces({
    kinds: ["project"],
    workspaceId: owner.sId,
    disabled: shouldWaitBeforeFetching || !hasFeature("projects"),
  });

  const moveConversationToProject = useMoveConversationToProject(owner);

  const joinConversation = useJoinConversation({
    ownerId: owner.sId,
    conversationId: activeConversationId,
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);

  const conversationLink = getConversationRoute(
    owner.sId,
    activeConversationId,
    undefined,
    config.getApiBaseUrl()
  );

  const doDelete = useDeleteConversation(owner);
  const leaveOrDelete = useCallback(
    async (forceDelete: boolean = false) => {
      const res = await doDelete(conversation, forceDelete);
      if (isConversationDisplayed && res) {
        const redirectRoute =
          conversation && isProjectConversation(conversation)
            ? getProjectRoute(owner.sId, conversation.spaceId)
            : getConversationRoute(owner.sId);
        void router.push(redirectRoute);
      }
    },
    [conversation, doDelete, owner.sId, router, isConversationDisplayed]
  );

  const copyConversationLink = useCallback(async () => {
    await navigator.clipboard.writeText(conversationLink ?? "");
    sendNotification({ type: "success", title: "Link copied !" });
  }, [conversationLink, sendNotification]);

  const openConversationInBrowser = () => {
    window.open(conversationLink, "_blank");
  };

  if (!activeConversationId) {
    return null;
  }

  const canJoin = conversationParticipationOptions.includes("join");
  const canLeave = conversationParticipationOptions.includes("leave");
  const canDelete = conversationParticipationOptions.includes("delete");

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      <DeleteConversationsDialog
        isOpen={showDeleteDialog}
        type="selection"
        selectedCount={1}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={() => {
          setShowDeleteDialog(false);
          void leaveOrDelete(true);
        }}
      />
      <LeaveConversationDialog
        isOpen={showLeaveDialog}
        onClose={() => setShowLeaveDialog(false)}
        onLeave={() => {
          setShowLeaveDialog(false);
          void leaveOrDelete();
        }}
      />
      <EditConversationTitleDialog
        isOpen={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        owner={owner}
        conversationId={activeConversationId}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        currentTitle={conversation?.title || ""}
      />
      <DropdownMenu modal={false} open={isOpen} onOpenChange={onOpenChange}>
        {triggerPosition ? (
          <>
            {trigger}
            <DropdownMenuTrigger asChild>
              <div
                style={{
                  position: "fixed",
                  left: triggerPosition.x,
                  top: triggerPosition.y,
                  width: 0,
                  height: 0,
                  pointerEvents: "none",
                }}
              />
            </DropdownMenuTrigger>
          </>
        ) : (
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent>
          <DropdownMenuItem
            label="Rename conversation"
            onClick={() => setShowRenameDialog(true)}
            icon={PencilSquareIcon}
          />
          {hasFeature("projects") && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                icon={ArrowRightIcon}
                label="Move to project"
                disabled={!projects.length}
              />
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {projects
                    .filter((project) => project.sId !== conversation?.spaceId)
                    .map((project) => (
                      <DropdownMenuItem
                        key={project.sId}
                        icon={getSpaceIcon(project)}
                        label={project.name}
                        onClick={async () =>
                          conversation
                            ? moveConversationToProject(conversation, project)
                            : Promise.resolve(false)
                        }
                      />
                    ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              icon={ContactsUserIcon}
              label="Participants"
              disabled={
                !conversationParticipants?.users.length &&
                !conversationParticipants?.agents.length &&
                !canJoin
              }
            />
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {canJoin && (
                  <>
                    <DropdownMenuItem
                      label="Join"
                      onClick={joinConversation}
                      icon={PlusCircleIcon}
                    />
                    <DropdownMenuSeparator />
                  </>
                )}
                {conversationParticipants?.agents.map((agent) => (
                  <DropdownMenuItem
                    key={agent.configurationId}
                    label={agent.name}
                    onClick={() => handleSeeAgentDetails(agent.configurationId)}
                    icon={
                      <Avatar
                        size="xs"
                        visual={agent.pictureUrl}
                        name={agent.name}
                      />
                    }
                  />
                ))}
                {conversationParticipants?.users.map((user) => (
                  <DropdownMenuItem
                    key={user.sId}
                    label={user.fullName ?? user.username}
                    onClick={() => handleSeeUserDetails(user.sId)}
                    icon={
                      <Avatar
                        size="xs"
                        visual={user.pictureUrl}
                        name={user.fullName ?? user.username}
                        isRounded
                      />
                    }
                    className="!text-foreground dark:!text-foreground-night"
                  />
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {displayOpenInBrowser && conversationLink && (
            <DropdownMenuItem
              label="Open in a browser tab"
              onClick={openConversationInBrowser}
              icon={ExternalLinkIcon}
            />
          )}
          {conversationLink && (
            <DropdownMenuItem
              label="Copy link"
              onClick={copyConversationLink}
              icon={LinkIcon}
            />
          )}
          {canTurnIntoAgent && (
            <DropdownMenuItem
              label="Shrinkwrap"
              icon={SidekickIcon}
              onClick={async () => {
                const confirmed = await confirm({
                  title: "Shrinkwrap",
                  message:
                    "This will open the agent builder and launch Sidekick on this conversation so you can turn it into an agent.",
                  validateLabel: "Continue",
                });
                if (confirmed && conversation) {
                  const route = getAgentBuilderRoute(
                    owner.sId,
                    "new",
                    `conversationId=${conversation.sId}`
                  );
                  void router.push(route);
                }
              }}
            />
          )}
          {canLeave && (
            <DropdownMenuItem
              label="Leave"
              onClick={() => setShowLeaveDialog(true)}
              icon={XMarkIcon}
            />
          )}
          {canDelete && (
            <DropdownMenuItem
              label="Delete"
              onClick={() => setShowDeleteDialog(true)}
              icon={TrashIcon}
              variant="warning"
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
